import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Navigate, Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';

const ENABLE_REFUNDS_FLAG = 'moniebook.pos.enableRefunds';
const ENABLE_BULK_COLLECT_PAYMENT = 'moniebook.pos.enableBulkCollectPayment';

export const ordersRoutes: RouteObject[] = [
    {
        path: 'sales',
        element: <StyleScope name='orders-ui' />,
        children: [
            {
                path: '',
                element: <Navigate to='completed' />,
            },
            {
                path: 'completed',
                element: (
                    <ProtectedView
                        accessCodeList={[
                            'VIEW_SALE',
                            'VIEW_COMPLETED_SALE',
                            'VIEW_OWN_COMPLETED_SALE',
                        ]}
                        grantAccessIfAny>
                        <Outlet />
                    </ProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { salesList } = await unwrapFeatures();
                            return salesList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':saleId/refund',
                        element: (
                            <FeatureProtectedView flag={ENABLE_REFUNDS_FLAG}>
                                <ProtectedView accessCodeList={['REFUND_SALE']}>
                                    <Outlet />
                                </ProtectedView>
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { refundSale } =
                                        await unwrapFeatures();
                                    return refundSale().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'outstanding',
                element: (
                    <ProtectedView
                        accessCodeList={[
                            'VIEW_OUTSTANDING_SALE',
                            'VIEW_OWN_OUTSTANDING_SALE',
                            'VIEW_SALE',
                        ]}
                        grantAccessIfAny>
                        <Outlet />
                    </ProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { outstandingSales } = await unwrapFeatures();
                            return outstandingSales().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'bulk-payment',
                        element: (
                            <FeatureProtectedView
                                flag={ENABLE_BULK_COLLECT_PAYMENT}>
                                <Outlet />
                            </FeatureProtectedView>
                        ),
                        async lazy() {
                            const { BulkPaymentPage } = await unwrapFeatures();
                            return BulkPaymentPage().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':saleId/refund',
                        element: (
                            <FeatureProtectedView flag={ENABLE_REFUNDS_FLAG}>
                                <ProtectedView accessCodeList={['REFUND_SALE']}>
                                    <Outlet />
                                </ProtectedView>
                            </FeatureProtectedView>
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { refundSale } =
                                        await unwrapFeatures();
                                    return refundSale().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'carts',
                element: (
                    <ProtectedView accessCodeList={['VIEW_SAVED_CART_HISTORY']}>
                        <Outlet />
                    </ProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { carts } = await unwrapFeatures();
                            return carts().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'refunds',
                element: (
                    <FeatureProtectedView flag={ENABLE_REFUNDS_FLAG}>
                        <ProtectedView
                            accessCodeList={[
                                'VIEW_SALE',
                                'VIEW_COMPLETED_SALE',
                                'VIEW_OWN_COMPLETED_SALE',
                            ]}
                            grantAccessIfAny>
                            <Outlet />
                        </ProtectedView>
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { refundList } = await unwrapFeatures();
                            return refundList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('orders/features');

    return features.default;
}
